import { MutationFunctionOptions } from '@apollo/client';
import {
  ArticleStructure,
  currentVersion,
  getEmptyStructure,
  getEmptyValue,
} from 'lib-knowledge-document-structure';
import {
  ArticleEditorDocument,
  ArticleEditorQuery,
  ArticleEditorQueryVariables,
  ArticleTranslationDocument,
  ArticleTranslationDraftCreateMutation,
  ArticleTranslationDraftCreateMutationVariables,
  ArticleTranslationQuery,
  ArticleTranslationQueryVariables,
  LanguageId,
  PublicationStatus,
  useArticleTranslationDraftCreateMutation,
} from 'lib-knowledge-management-common/graphql';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

type MutationFunction = ReturnType<
  typeof useArticleTranslationDraftCreateMutation
>[0];
type MutationReturnValue = ReturnType<MutationFunction>;

export type CreateTranslationFn = (options: {
  languageId: LanguageId;
  onCompleted?: Options['onCompleted'];
  onQueryUpdated?: Options['onQueryUpdated'];
}) => MutationReturnValue;

type UseTranslationCreateReturnValue = [
  CreateTranslationFn,
  ReturnType<typeof useArticleTranslationDraftCreateMutation>[1],
];

type Options = MutationFunctionOptions<
  ArticleTranslationDraftCreateMutation,
  ArticleTranslationDraftCreateMutationVariables
>;

type UseTranslationCreateParams = {
  articleId: string;
};

export function useTranslationCreate({
  articleId,
}: UseTranslationCreateParams): UseTranslationCreateReturnValue {
  const [createTranslation, mutationResult] =
    useArticleTranslationDraftCreateMutation();
  const create: CreateTranslationFn = useCallback(
    ({ languageId, onCompleted, onQueryUpdated }) => {
      const content: ArticleStructure = {
        version: currentVersion,
        children: getEmptyValue(),
      };

      return createTranslation({
        onCompleted,
        onQueryUpdated,
        awaitRefetchQueries: false,
        variables: {
          articleId,
          languageId,
          content,
          title: 'Untitled',
        },

        optimisticResponse: {
          __typename: 'Mutations',
          articleTranslationCreate: {
            __typename: 'ArticleTranslation',
            articleId,
            languageId,
            version: { __typename: 'Draft' },
            title: '',
            titleTranslatedAt: null,
            titleUpdatedAt: null,
            content: getEmptyStructure(),
            updatedAt: Date.now(),
            updatedBy: null,
            publicationStatus: PublicationStatus.NeverPublished,
            seoTitle: null,
            metaDescription: null,
            slug: null,
            effectiveSlug: null,
            path: `/${languageId}/article/${articleId.slice(-6)}`,
          },
        },

        update: (cache, { data }) => {
          if (!data?.articleTranslationCreate) return;

          const cachedArticleEditorQuery = cache.readQuery<
            ArticleEditorQuery,
            ArticleEditorQueryVariables
          >({
            query: ArticleEditorDocument,
            variables: { id: articleId },
          });
          if (!cachedArticleEditorQuery?.article) return;

          const updatedArticleQuery: ArticleEditorQuery = {
            ...cachedArticleEditorQuery,
            article: {
              ...cachedArticleEditorQuery.article,
              translations: {
                ...cachedArticleEditorQuery.article.translations,
                draft: [
                  ...cachedArticleEditorQuery.article.translations.draft,
                  data.articleTranslationCreate,
                ],
              },
            },
          };

          cache.writeQuery<ArticleEditorQuery, ArticleEditorQueryVariables>({
            query: ArticleEditorDocument,
            variables: { id: articleId },
            data: updatedArticleQuery,
          });

          // Article Translation Query
          cache.writeQuery<
            ArticleTranslationQuery,
            ArticleTranslationQueryVariables
          >({
            query: ArticleTranslationDocument,
            variables: {
              id: articleId,
              languageId,
            },
            data: {
              __typename: 'Queries',
              articleTranslation: {
                __typename: 'TranslationQuery',
                articleId,
                publicationStatus: PublicationStatus.NeverPublished,
                draft: {
                  ...data.articleTranslationCreate,
                },
              },
            },
          });
        },
      }).then((args) => {
        ampli.knowledgeArticleUpdated({ articleId });
        return args;
      });
    },
    [createTranslation, articleId],
  );

  return [create, mutationResult];
}
